// @flow
import React from "react";
import type { UnaryFn, BinaryFn, NullaryFn } from "@tvg/types/Functional";
import { noop, get } from "lodash";
import {
  AlertInline,
  Button,
  Header,
  InputNumber,
  Radio,
  InputSelect
} from "@tvg/design-system";
import type { WagerGroupsItem } from "@tvg/types/Wager";

import {
  DefaultBetContainer,
  DefaultSection,
  BetGroupLine,
  CurrencySymbol,
  WagerSelectContainer,
  CustomBetListSection
} from "./styled-components";
import PreferencesToggleGroup from "../../_molecule/PreferencesToggleGroup";

type WagerType = {
  type: {
    id: number,
    name: string
  }
};

type Props = {
  /**
   * default bet type
   */
  defaultBetType: string,
  /**
   * list of all available wager types
   */
  wagerTypes: WagerType[],
  /**
   * callback for selecting a wager
   */
  onSelectWagerType: BinaryFn<string, string, void>,
  /**
   * list of main wager categories for custom bet amount preferences
   */
  wagerGroups: Array<WagerGroupsItem>,
  /**
   * if a list of main wager categories for custom bet amount preferences is shown
   */
  isMainWagerGroupsShown: boolean,
  /**
   * Callback for changing default bet type option
   */
  onOptionChange: UnaryFn<SyntheticKeyboardEvent<*>, void>,
  /**
   * Callback for onKeyDown event in amount input
   */
  onKeyDown: UnaryFn<SyntheticKeyboardEvent<*>, void>,
  /**
   * Callback for onBlur event in amount input
   */
  onInputBlur: UnaryFn<WagerGroupsItem, void>,
  /**
   * Update bet amount callback
   */
  updateBetAmount: BinaryFn<string, WagerGroupsItem, void>,
  /**
   * Update bet confirmation toggle
   */
  updateShowBetConfirm: NullaryFn<void>,
  /**
   * if bet confirmation toggle is on
   */
  showBetConfirm: string,
  /**
   * Text capi
   */
  bettingCapi: {
    betConfirmation: {
      title: string,
      description: string,
      label: string
    },
    betType: {
      title: string
    },
    betAmount: {
      title: string,
      label: Array<string>,
      message: string,
      placeholder: string
    },
    resetButton: string
  },
  /**
   * Boolean to decided if user still has betting preferences default
   */
  hasDefaultBettingPreferences: boolean,
  /**
   * Function to handle the on click of reset preferences
   */
  handleResetPreferences: NullaryFn<void>,
  /**
   * Value to control loading of bet confirmation
   */
  isLoadingBetConfirmation: boolean
};

const BettingPreferences = ({
  defaultBetType,
  onSelectWagerType,
  onOptionChange,
  onKeyDown,
  onInputBlur,
  updateBetAmount,
  isMainWagerGroupsShown,
  wagerTypes,
  wagerGroups,
  updateShowBetConfirm,
  showBetConfirm,
  bettingCapi,
  hasDefaultBettingPreferences,
  handleResetPreferences,
  isLoadingBetConfirmation
}: Props) => {
  const renderBetConfirmation = () => (
    <PreferencesToggleGroup
      qaLabel="bet-confirmation-container"
      title={bettingCapi.betConfirmation.title}
      description={bettingCapi.betConfirmation.description}
      toggles={[
        {
          name: bettingCapi.betConfirmation.label,
          onClick: updateShowBetConfirm,
          defaultValue: showBetConfirm === "1",
          qaLabel: "bet-confirmation",
          isLoading: isLoadingBetConfirmation
        }
      ]}
    />
  );

  const mappedTypes = wagerTypes.map((wager) => ({
    label: wager.type.name,
    value: wager.type.id
  }));

  const renderDefaultBetType = () => (
    <DefaultSection data-qa-label="bet-preference-type-section">
      <Header tag="h3" qaLabel="bet-preference-type-label">
        {get(bettingCapi, "betType.title", "")}
      </Header>
      <WagerSelectContainer>
        <InputSelect
          qaLabel="bet-preference-type-wager"
          selectedValue={+defaultBetType}
          onChange={(val) => {
            const text = wagerTypes.reduce(
              (acc, currentValue) =>
                currentValue.type.id === +val ? currentValue.type.name : acc,
              ""
            );
            onSelectWagerType(val, text);
          }}
          selectList={mappedTypes}
        />
      </WagerSelectContainer>
    </DefaultSection>
  );

  const renderCustomBetAmountList = (wagerList: Array<WagerGroupsItem>) => (
    <CustomBetListSection data-qa-label="bet-preference-custom-bet-list-section">
      <AlertInline
        message={bettingCapi.betAmount.message}
        qaLabel="bet-preference-custom-bet-list-alert"
        variant="informational"
      />
      {wagerList.map((wagerItem: WagerGroupsItem) => {
        let title =
          wagerItem.title.charAt(0).toUpperCase() + wagerItem.title.slice(1);
        if (wagerItem.title === "pick") {
          title = `${title} (all pick bets)`;
        }

        return (
          <BetGroupLine
            key={wagerItem.title}
            data-qa-label={`bet-preference-custom-bet-list-bet-group-${wagerItem.title}`}
          >
            <Header
              tag="h4"
              qaLabel="bet-preference-custom-bet-list-bet-group-title"
            >
              {title}
            </Header>
            <InputNumber
              qaLabel={`wager-amount-${wagerItem.group}`}
              placeholder={bettingCapi.betAmount.placeholder}
              value={wagerItem.value}
              onChangeText={(value) => {
                updateBetAmount(value, wagerItem);
              }}
              onBlur={() => onInputBlur(wagerItem)}
              start={
                wagerItem.value !== "" && (
                  <CurrencySymbol data-qa-label="bet-preference-custom-bet-list-symbol">
                    $
                  </CurrencySymbol>
                )
              }
              onKeyPress={(e) => onKeyDown(e)}
              hasDecimalPlaces
              width={212}
            />
          </BetGroupLine>
        );
      })}
    </CustomBetListSection>
  );

  const renderBetAmountOptions = () => (
    <DefaultSection data-qa-label="bet-preference-amount-options-section">
      <Header
        tag="h3"
        qaLabel="bet-preference-amount-options-label"
        mb="space-5"
      >
        {get(bettingCapi, "betAmount.title", "")}
      </Header>
      <Radio.Group
        qaLabel="bet-preference-amount-container-radio-group"
        selectedValue={
          isMainWagerGroupsShown ? "useCustomBetAmount" : "useMinimumBetAmount"
        }
        onChange={(val) => onOptionChange(val)}
      >
        <Radio
          value="useMinimumBetAmount"
          label={get(bettingCapi, "betAmount.label[0]", "")}
          qaLabel="bet-preference-amount-section-checkbox-use-minimum-amount"
        />
        <Radio
          value="useCustomBetAmount"
          label={get(bettingCapi, "betAmount.label[1]", "")}
          qaLabel="bet-preference-amount-section-checkbox-use-custom-amount"
        />
      </Radio.Group>

      {isMainWagerGroupsShown && renderCustomBetAmountList(wagerGroups)}
      {!hasDefaultBettingPreferences && (
        <Button
          variant="tertiary"
          onPress={handleResetPreferences}
          qaLabel="betting-reset-button"
          isStretched
          px={16}
          mt={20}
        >
          {bettingCapi.resetButton}
        </Button>
      )}
    </DefaultSection>
  );

  return (
    <DefaultBetContainer data-qa-label="bet-preference-wrap">
      {renderBetConfirmation()}
      {renderDefaultBetType()}
      {renderBetAmountOptions()}
    </DefaultBetContainer>
  );
};

BettingPreferences.defaultProps = {
  defaultBetType: "0",
  onSelectWagerType: noop,
  onOptionChange: noop,
  onKeyDown: noop,
  onInputBlur: noop,
  updateBetAmount: noop,
  isMainWagerGroupsShown: false,
  wagerTypes: [],
  wagerGroups: [],
  bettingCapi: {
    betConfirmation: {
      title: "",
      description: "",
      label: ""
    },
    betType: {
      title: ""
    },
    betAmount: {
      title: "",
      label: [],
      message: "",
      placeholder: ""
    },
    reset: {
      text: ""
    }
  },
  handleResetPreferences: noop
};

export default BettingPreferences;
